<template>
  <div
    class="Input"
    :class="[
      parentClass,
      {
        'is-filled': Boolean(inputText),
        'is-error': hasError,
        'is-success': success && !hasError,
        'is-static': static,
        'is-disabled': disabled,
      },
    ]"
  >
    <FieldLabel
      v-if="label"
      :for="inputId"
      :required="isRequired"
      :hideAsterisk
      :disabled
    >
      {{ label }}
    </FieldLabel>

    <div class="Input-field" @click="focusInput">
      <Icon v-if="prefixIcon" :icon="prefixIcon" class="Input-prefixIcon" />

      <slot
        :id="inputId"
        :value="inputText"
        :attrs="attrs"
        :disabled="disabled"
        :readonly="static || readonly"
        @update:modelValue="onValueChange"
      >
        <input
          :id="inputId"
          ref="inputRef"
          v-bind="attrs"
          :value="inputText"
          :type="type"
          :disabled
          :readonly="static || readonly"
          :required="isRequired"
          @input="onValueChange"
        >
      </slot>

      <div v-if="suffixIcon || showSuccessIcon" class="Input-suffixIcon">
        <Icon v-if="suffixIcon" :icon="suffixIcon" />
        <Icon v-if="showSuccessIcon" icon="check-circle" class="Input-successIcon" />
      </div>
    </div>

    <FieldHelper
      :errorMessage="errorMessage"
      :helperText="helperText"
    />
  </div>
</template>

<script setup lang="ts" generic="T extends string | number = string">
import { computed, useAttrs, useId, useTemplateRef } from 'vue';
import { useFieldValidation, type FieldProps } from '@cvo/ui/composables';
import type { IconName } from '../Icon/Icon.model';
import FieldLabel from '../FieldLabel/FieldLabel.vue';
import Icon from '../Icon/Icon.vue';
import FieldHelper from '../FieldHelper/FieldHelper.vue';

export interface InputProps extends FieldProps {
  id?: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'number' | 'search' | 'date' | 'time';
  success?: boolean;
  readonly?: boolean;
  prefixIcon?: IconName;
  suffixIcon?: IconName;
  /**
   * Formats the input value as the user types.
   * @param raw The native input value entered by the user.
   */
  formatter?: (raw: string) => string;
}

const props = withDefaults(defineProps<InputProps>(), {
  type: 'text',
  error: undefined,
});

const inputText = defineModel<T>({ default: '' as T });

const { hasError, errorMessage, isRequired } = useFieldValidation(props);

const inputRef = useTemplateRef('inputRef');
const { class: parentClass, ...attrs } = useAttrs();

const inputId = computed(() => props.id || useId());

const showSuccessIcon = computed(() => props.success && !hasError.value);

function onValueChange(event: Event) {
  const target = event.target as HTMLInputElement;
  if (props.formatter) {
    const formattedValue = props.formatter(target.value);
    target.value = formattedValue;
    inputText.value = formattedValue as T; // Update the input's inner value with the formatted one.
  } else {
    inputText.value = target.value as T;
  }
}

function focusInput() {
  if (props.disabled || props.static) return;
  inputRef.value?.focus();
}

defineOptions({
  inheritAttrs: false,
});

defineExpose({
  inputRef,
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';

@mixin input-state(
  $label-color,
  $bg-color,
  $border-color,
  $icon-color,
  $helper-color,
) {
  // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
  --field-label-color: #{$label-color};
  .Input-field {
    background-color: $bg-color;
    border-color: $border-color;

    .Input-prefixIcon,
    .Input-suffixIcon {
      color: $icon-color;
    }
  }
  .FieldHelper {
    color: $helper-color;
  }
}

@layer cvo-ui {
  .Input {
    // TODO CVO-18267: Replace with generic field label tokens once updated in Figma
    --field-label-color: #{vars.$atoms-input-enabled-label-color};
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-2;
    width: 100%;

    &-field {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-1;
      background-color: vars.$atoms-input-enabled-bg-color;
      border: vars.$atoms-input-border-stroke solid vars.$atoms-input-enabled-border-color;
      border-radius: vars.$atoms-input-border-radius;
      padding: vars.$atoms-input-padding-top-bottom vars.$atoms-input-padding-left-right;
      overflow: hidden; // Ensure content respects border-radius
      width: 100%;

      input {
        @include text.text('sm');
        flex: 1;
        border: none;
        outline: none;
        background: transparent;
        min-width: 0;
        min-height: 20px;
        height: 100%;
        padding: 0;
        margin: 0;
        color: vars.$atoms-input-text-color;

        &::placeholder {
          color: vars.$atoms-input-placeholder-color;
        }
      }
    }

    &-prefixIcon,
    &-suffixIcon {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-shrink: 0;
      color: vars.$atoms-input-enabled-icon-color;
    }

    .FieldHelper {
      color: vars.$atoms-input-enabled-helper-color;
    }

    &.is-filled {
      @include input-state(
        $label-color: vars.$atoms-input-filled-label-color,
        $bg-color: vars.$atoms-input-filled-bg-color,
        $border-color: vars.$atoms-input-filled-border-color,
        $icon-color: vars.$atoms-input-filled-icon-color,
        $helper-color: vars.$atoms-input-filled-helper-color,
      );
    }

    &:focus-within {
      @include input-state(
        $label-color: vars.$atoms-input-focused-label-color,
        $bg-color: vars.$atoms-input-focused-bg-color,
        $border-color: vars.$atoms-input-focused-border-color,
        $icon-color: vars.$atoms-input-focused-icon-color,
        $helper-color: vars.$atoms-input-focused-helper-color,
      );
    }

    /* Component States */
    &.is-success {
      @include input-state(
        $label-color: vars.$atoms-input-success-label-color,
        $bg-color: vars.$atoms-input-success-bg-color,
        $border-color: vars.$atoms-input-success-border-color,
        $icon-color: vars.$atoms-input-success-icon-color,
        $helper-color: vars.$atoms-input-success-helper-color,
      );

      .Input-successIcon {
        color: vars.$atoms-input-check-color;
      }
    }

    &.is-error {
      @include input-state(
        $label-color: vars.$atoms-input-error-label-color,
        $bg-color: vars.$atoms-input-error-bg-color,
        $border-color: vars.$atoms-input-error-border-color,
        $icon-color: vars.$atoms-input-error-icon-color,
        $helper-color: vars.$atoms-input-error-helper-color,
      );
    }

    &.is-disabled {
      @include input-state(
        $label-color: vars.$atoms-input-disabled-label-color,
        $bg-color: vars.$atoms-input-disabled-bg-color,
        $border-color: vars.$atoms-input-disabled-border-color,
        $icon-color: vars.$atoms-input-disabled-icon-color,
        $helper-color: vars.$atoms-input-disabled-helper-color,
      );

      .Input-field {
        cursor: not-allowed;

        input {
          pointer-events: none;
        }
      }

      &.is-error {
        .Input-field {
          border-color: vars.$atoms-input-error-border-color,
        }
        .FieldHelper {
          color: vars.$atoms-input-error-helper-color;
        }
      }
      &.is-success {
        .Input-field {
          border-color: vars.$atoms-input-success-border-color;
        }
        .FieldHelper {
          color: vars.$atoms-input-success-helper-color;
        }
      }
    }

    &.is-static {
      @include input-state(
        $label-color: vars.$atoms-input-static-label-color,
        $bg-color: vars.$atoms-input-static-bg-color,
        $border-color: vars.$atoms-input-static-border-color,
        $icon-color: vars.$atoms-input-static-icon-color,
        $helper-color: vars.$atoms-input-static-helper-color,
      );
    }
  }
}
</style>
